import React, { useEffect, useRef, useState } from 'react'
import { Box, SxProps, Theme } from '@mui/material'

type RevealOnScrollProps = {
  children: React.ReactNode
  /** Delay before animation starts (ms) */
  delay?: number
  /** How far the block travels on enter */
  offset?: number
  once?: boolean
  sx?: SxProps<Theme>
}

/**
 * Lightweight scroll-reveal (IntersectionObserver + CSS).
 * Avoids framer-motion in the critical path.
 */
const RevealOnScroll: React.FC<RevealOnScrollProps> = ({
  children,
  delay = 0,
  offset = 28,
  once = true,
  sx,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const node = ref.current
    if (!node) return

    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (prefersReduced) {
      setVisible(true)
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true)
          if (once) observer.disconnect()
        } else if (!once) {
          setVisible(false)
        }
      },
      {
        threshold: 0.12,
        rootMargin: '0px 0px -8% 0px',
      }
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [once])

  return (
    <Box
      ref={ref}
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translate3d(0,0,0)' : `translate3d(0, ${offset}px, 0)`,
        transition: `opacity 0.65s ease ${delay}ms, transform 0.65s ease ${delay}ms`,
        willChange: 'opacity, transform',
        ...((sx as object) || {}),
      }}>
      {children}
    </Box>
  )
}

export default RevealOnScroll
